import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { MapView } from '../../components/common/MapView';
import { Button } from '../../components/ui/button';
import { Footprints, Loader2 } from 'lucide-react';
import { api } from '../../lib/supabase';
import { toast } from 'sonner';
import { computePace, formatDuration } from '../../lib/formatters';
import { useUnits, kmToDisplay, unitLabel, paceSecPerUnit } from '../../lib/units';
import { ShareResultButton } from '../../components/common/ShareResultButton';
import { useAuth } from '../../lib/auth-context';

// Receives elapsed time + distance via session storage from LivePersonalRun
export default function RunSummary() {
  const nav = useNavigate();
  const { units } = useUnits();
  const { profile } = useAuth();
  const [elapsed] = useState(() => Number(sessionStorage.getItem('rivl:run:elapsed') || 1938));
  const [distanceKm] = useState(() => Number(sessionStorage.getItem('rivl:run:distance') || 6.32));
  const [gpsTrack] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('rivl:run:track') || 'null'); } catch { return null; }
  });
  const [startedAt] = useState(() => {
    const v = sessionStorage.getItem('rivl:run:startedAt');
    return v ? Number(v) : null;
  });
  const [visibility] = useState(() => {
    const v = sessionStorage.getItem('rivl:run:visibility');
    return ['private', 'friends', 'public'].includes(v) ? v : 'private';
  });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const savedRef = useRef(false);

  // Auto-save once on mount — but skip "ghost" runs under 7 seconds, which
  // are almost always accidental taps (start → finish without actually running).
  const MIN_DURATION_SEC = 7;
  const tooShort = elapsed < MIN_DURATION_SEC;
  const hasGpsTrack = !tooShort && Array.isArray(gpsTrack) && gpsTrack.length > 1;
  // Pace sanity — same guards as LivePersonalRun (see /pages/mobile/LivePersonalRun.jsx).
  // Don't display pace until >= 50 m tracked, and drop implausibly slow paces (> 25 min/km).
  const distanceMeters = distanceKm * 1000;
  const paceKmSecRaw = distanceMeters >= 50 && elapsed > 0 ? Math.floor(elapsed / distanceKm) : null;
  const paceKmSec = paceKmSecRaw && paceKmSecRaw <= 25 * 60 ? paceKmSecRaw : null;
  const paceUnitSec = paceKmSec ? Math.round(paceSecPerUnit(paceKmSec, units)) : null;
  const paceLabel = paceUnitSec ? `${Math.floor(paceUnitSec / 60)}:${String(paceUnitSec % 60).padStart(2, '0')}` : '--:--';
  const timeLabel = formatDuration(elapsed);
  const distanceDisplay = kmToDisplay(distanceKm, units).toFixed(2);

  useEffect(() => {
    if (savedRef.current) return;
    savedRef.current = true;
    if (elapsed < MIN_DURATION_SEC) {
      // Discard — don't save, don't toast as error. Just clear session storage.
      sessionStorage.removeItem('rivl:run:elapsed');
      sessionStorage.removeItem('rivl:run:distance');
      sessionStorage.removeItem('rivl:run:track');
      sessionStorage.removeItem('rivl:run:startedAt');
      sessionStorage.removeItem('rivl:run:visibility');
      try { localStorage.removeItem('rivl:active-run'); } catch { /* private mode */ }
      return;
    }
    setBusy(true);
    api.savePersonalRun({ distanceKm, durationSec: elapsed, visibility, gpsTrack, startedAt })
      .then(() => {
        setSaved(true);
        sessionStorage.removeItem('rivl:run:elapsed');
        sessionStorage.removeItem('rivl:run:distance');
        sessionStorage.removeItem('rivl:run:track');
        sessionStorage.removeItem('rivl:run:startedAt');
        sessionStorage.removeItem('rivl:run:visibility');
        try { localStorage.removeItem('rivl:active-run'); } catch { /* private mode */ }
      })
      .catch((err) => toast.error(err.message || 'Failed to save run'))
      .finally(() => setBusy(false));
  }, [distanceKm, elapsed, gpsTrack, visibility, startedAt]);

  return (
    <div className="px-4 pt-4 pb-6 space-y-5" data-testid="run-summary">
      <div className="text-center pt-4">
        <div className="mx-auto h-14 w-14 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
          <Footprints className="h-7 w-7" />
        </div>
        <p className="rivl-label mt-3">
          {tooShort ? 'Personal Run · Not saved' : busy ? 'Saving…' : (saved ? 'Personal Run · Saved' : 'Personal Run')}
        </p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
          {tooShort ? 'Too short to count.' : 'Nice run.'}
        </h1>
        {tooShort && (
          <p className="text-xs text-muted-foreground mt-2" data-testid="run-too-short-hint">
            Runs under 7 seconds aren&apos;t recorded.
          </p>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat label="Distance" value={distanceDisplay} unit={unitLabel(units)} />
        <Stat label="Time" value={timeLabel} />
        <Stat label="Pace" value={paceLabel} unit={`/${unitLabel(units)}`} />
      </div>

      {hasGpsTrack ? (
        <MapView
          height={240}
          path={gpsTrack}
          showCurrent
          currentIndex={gpsTrack.length - 1}
          data-testid="run-summary-map"
        />
      ) : (
        <div
          className="rivl-card p-6 text-center text-xs text-muted-foreground"
          data-testid="run-summary-no-track"
        >
          No GPS track was recorded for this run. Make sure location permission is
          enabled and the screen stays unlocked on your next run.
        </div>
      )}

      <div className="rivl-card p-4 space-y-2 text-sm">
        <Row k="Visibility" v={visibility.charAt(0).toUpperCase() + visibility.slice(1)} />
        <Row k="Verified" v="No (personal run)" />
      </div>

      {!tooShort && (
        <ShareResultButton
          className="h-11 rounded-md w-full bg-primary hover:bg-primary/90 text-primary-foreground"
          testid="run-summary-share"
          label="Share your finish"
          stats={{
            name: profile?.name || 'A RIVL runner',
            distanceKm,
            durationSec: elapsed,
            paceSecPerKm: paceKmSec,
            verified: false,
            dateISO: new Date().toISOString(),
            path: gpsTrack,
          }}
        />
      )}

      <div className="grid grid-cols-2 gap-3">
        <Button asChild variant="outline" className="h-11 rounded-md">
          <Link to="/profile" data-testid="summary-history">View history</Link>
        </Button>
        <Button asChild className="h-11 rounded-md">
          <Link to="/" data-testid="summary-done">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Done'}</Link>
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value, unit }) {
  return (
    <div className="rivl-card py-3 text-center">
      <div className="font-mono font-bold text-2xl tabular-nums">{value}</div>
      <div className="rivl-label text-[9px]">{label}{unit ? ` (${unit})` : ''}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium">{v}</span>
    </div>
  );
}
